import * as THREE from "three";
import { getSceneState } from "../../stores/sceneStore";
import { sampleHFieldHeightAtModelXY } from "../../utils/hfieldModelSampling";
import { getStandardFloorHField } from "../../utils/terrainFloor";
import { configureRaycasterForScenePicking } from "../scene/scenePicking";
import { intersectLocalRayWithHField } from "../scene/sceneMisc";

export function resolvePlacementFrame({
  modelGroupRef,
  sceneRef,
  fallbackPlacementFrameRef,
  useYUp,
}) {
  if (modelGroupRef.current) return modelGroupRef.current;
  const inScene = sceneRef.current?.getObjectByName("modelGroup");
  if (inScene) return inScene;
  if (!fallbackPlacementFrameRef.current) {
    const proxy = new THREE.Group();
    if (!useYUp) proxy.rotation.x = -Math.PI / 2;
    proxy.updateMatrixWorld(true);
    fallbackPlacementFrameRef.current = proxy;
  }
  return fallbackPlacementFrameRef.current;
}

export function getModelGroundPlaneWorld({ placementFrame, dropPlane }) {
  if (!placementFrame) return null;
  const worldOrigin = new THREE.Vector3();
  const worldQuat = new THREE.Quaternion();
  const worldNormal = new THREE.Vector3(0, 0, 1);
  placementFrame.getWorldPosition(worldOrigin);
  placementFrame.getWorldQuaternion(worldQuat);
  worldNormal.copy(new THREE.Vector3(0, 0, 1).applyQuaternion(worldQuat).normalize());
  dropPlane.setFromNormalAndCoplanarPoint(worldNormal, worldOrigin);
  return dropPlane;
}

export function getGroundWorldPointFromViewport({ u, v, camera, placementFrame, dropPlane }) {
  if (!camera) return null;
  const plane = getModelGroundPlaneWorld({ placementFrame, dropPlane });
  if (!plane) return null;
  const pointer = new THREE.Vector2(u * 2 - 1, -(v * 2 - 1));
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(pointer, camera);
  const worldPoint = new THREE.Vector3();
  if (raycaster.ray.intersectPlane(plane, worldPoint)) return worldPoint;
  return null;
}

export function convertGroundWorldPointToModelLocal(worldPoint, placementFrame) {
  if (!worldPoint || !placementFrame) return null;
  const localPoint = worldPoint.clone();
  placementFrame.worldToLocal(localPoint);
  return [localPoint.x, localPoint.y, localPoint.z];
}

export function getLibraryDropSupportHitFromViewport({
  u,
  v,
  camera,
  scene,
  placementFrame,
  sceneObjectsRef,
}) {
  if (!scene || !camera || !placementFrame) return null;

  camera.updateMatrixWorld(true);
  scene.updateMatrixWorld(true);

  const pointer = new THREE.Vector2(u * 2 - 1, -(v * 2 - 1));
  const raycaster = new THREE.Raycaster();
  configureRaycasterForScenePicking(raycaster);
  raycaster.setFromCamera(pointer, camera);

  const upWorld = new THREE.Vector3(0, 0, 1);
  const frameQuat = new THREE.Quaternion();
  placementFrame.getWorldQuaternion(frameQuat);
  upWorld.applyQuaternion(frameQuat).normalize();

  const supportBlockedNames = new Set([
    "sceneGrid",
    "libraryDropShadow",
    "libraryGripperAttachMarkers",
    "meshToolkitWireframe",
    "faceColors",
  ]);

  const getSupportBodyUuid = (object) => {
    let cur = object;
    while (cur) {
      const uuid = cur.userData?.bodyUuid;
      if (uuid && sceneObjectsRef.current?.[uuid]) return uuid;
      cur = cur.parent;
    }
    return null;
  };

  const isDescendantNamed = (object, names) => {
    let cur = object;
    while (cur) {
      if (names.has(cur.name)) return true;
      cur = cur.parent;
    }
    return false;
  };

  const isSupportCandidateObject = (object) => {
    if (!object?.isMesh || !object.visible || !object.geometry) return false;
    if (isDescendantNamed(object, supportBlockedNames)) return false;
    const ud = object.userData || {};
    if (
      ud.isGlow ||
      ud.isJointArc ||
      ud.isJointEndpoint ||
      ud.isInertial ||
      ud.isPositionGizmo ||
      ud.isRotationGizmo ||
      ud.isScaleGizmo ||
      ud.type === "site" ||
      ud.type === "inertial"
    ) {
      return false;
    }
    if (object.name === "sceneFloor") return true;
    if (ud.type === "mesh" || ud.type === "visual" || ud.type === "collision") return true;
    return Boolean(getSupportBodyUuid(object));
  };

  const normalMatrix = new THREE.Matrix3();
  const worldNormal = new THREE.Vector3();
  const localPoint = new THREE.Vector3();
  const intersects = raycaster.intersectObjects(scene.children, true);
  for (const hit of intersects) {
    const object = hit.object;
    if (!isSupportCandidateObject(object)) continue;
    if (!hit.face) continue;
    object.updateWorldMatrix(true, false);
    normalMatrix.getNormalMatrix(object.matrixWorld);
    worldNormal.copy(hit.face.normal).applyMatrix3(normalMatrix).normalize();
    if (worldNormal.dot(upWorld) < 0.35) continue;
    localPoint.copy(hit.point);
    placementFrame.worldToLocal(localPoint);
    const supportBodyUuid = getSupportBodyUuid(object);
    return {
      worldPoint: hit.point.clone(),
      worldNormal: worldNormal.clone(),
      modelPoint: [localPoint.x, localPoint.y, localPoint.z],
      supportZ: localPoint.z,
      supportObjectId: object.id,
      supportObjectName: object.name === "sceneFloor" ? "sceneFloor" : "",
      supportBodyUuid,
    };
  }
  return null;
}

export function resolveDropPlacementFromViewport({
  u,
  v,
  allowViewportFallback = false,
  camera,
  scene,
  placementFrame,
  sceneObjectsRef,
  dropPlane,
}) {
  const supportHit = getLibraryDropSupportHitFromViewport({
    u,
    v,
    camera,
    scene,
    placementFrame,
    sceneObjectsRef,
  });
  const floorHField = getStandardFloorHField(getSceneState()?.hfields);
  let terrainRayModelPoint = null;
  if (!supportHit && floorHField && camera) {
    const pointerNdc = new THREE.Vector2(u * 2 - 1, -(v * 2 - 1));
    const terrainRaycaster = new THREE.Raycaster();
    terrainRaycaster.setFromCamera(pointerNdc, camera);
    const localOrigin = terrainRaycaster.ray.origin.clone();
    placementFrame.worldToLocal(localOrigin);
    const frameQuat = new THREE.Quaternion();
    placementFrame.getWorldQuaternion(frameQuat);
    const localDirection = terrainRaycaster.ray.direction
      .clone()
      .applyQuaternion(frameQuat.invert())
      .normalize();
    terrainRayModelPoint = intersectLocalRayWithHField(localOrigin, localDirection, floorHField);
  }

  let worldPoint = supportHit?.worldPoint || null;
  if (!worldPoint && terrainRayModelPoint) {
    worldPoint = terrainRayModelPoint.clone();
    placementFrame.localToWorld(worldPoint);
  }
  if (!worldPoint) {
    worldPoint = getGroundWorldPointFromViewport({ u, v, camera, placementFrame, dropPlane });
  }
  if (!worldPoint && allowViewportFallback) {
    worldPoint = getGroundWorldPointFromViewport({
      u: 0.5,
      v: 0.62,
      camera,
      placementFrame,
      dropPlane,
    });
  }
  if (!worldPoint) return null;

  const modelPoint =
    supportHit?.modelPoint ||
    (terrainRayModelPoint
      ? [terrainRayModelPoint.x, terrainRayModelPoint.y, terrainRayModelPoint.z]
      : convertGroundWorldPointToModelLocal(worldPoint, placementFrame));
  if (!modelPoint) return null;

  const terrainHeight = floorHField
    ? sampleHFieldHeightAtModelXY(floorHField, modelPoint[0], modelPoint[1])
    : null;
  const shouldUseTerrainHeight =
    terrainHeight != null &&
    Number.isFinite(terrainHeight) &&
    (!supportHit || supportHit.supportObjectName === "sceneFloor");
  if (shouldUseTerrainHeight) {
    modelPoint[2] = terrainHeight;
    worldPoint = new THREE.Vector3(modelPoint[0], modelPoint[1], modelPoint[2]);
    placementFrame.localToWorld(worldPoint);
  }

  const supportZ = shouldUseTerrainHeight
    ? terrainHeight
    : Number.isFinite(supportHit?.supportZ)
      ? supportHit.supportZ
      : modelPoint[2];
  const supportObjectName =
    shouldUseTerrainHeight || terrainRayModelPoint
      ? "sceneFloor"
      : supportHit?.supportObjectName || "";

  return {
    modelPoint,
    worldPoint,
    support: {
      z: supportZ,
      objectId: supportHit?.supportObjectId ?? null,
      objectName: supportObjectName,
      bodyUuid: supportHit?.supportBodyUuid || null,
    },
    supportHit,
    terrainRayModelPoint,
    terrainHeight,
    shouldUseTerrainHeight,
  };
}
